import { Fragment } from "react/jsx-runtime";
import CloseIcon from "../../assets/svg/CloseIcon";
import { useHistory } from "react-router-dom";
import { useRef, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import toastMessages from "../../services/utils/helpers/toastMessage";
import { Formik, Form } from "formik";
import validations from "../../services/validations";
import AuthorizePhysicianFields from "../formsFields/AuthorizeAsPhysicianFields";
import CustomText from "../atomic/CustomText";
import Button from "../organism/Button";
import axios from "axios";
import FileUploader from "../global/FileUploader";
import useGetPresignedUrl from "../../services/utils/custom hooks/Documents/useGetPresignedUrl";
import useAddAuthorizedDocuments from "../../services/utils/custom hooks/Approvals/useAddAuthorizedDocuments";
import useClassifyAnalyzeDocumentPatientInfoCheck from "../../services/utils/custom hooks/Approvals/useClassifyAnalyzeDocumentPatientInfoCheck";
import useSendForApproval from "../../services/utils/custom hooks/Approvals/useSendForApproval";
import BlankDocumentSubmissionBlockedModal from "./BlankDocumentSubmissionBlockedModal";

function AuthorizeAsPhysician({
  ids,
  hasIDG,
}: {
  ids: any[];
  hasIDG: boolean;
}): JSX.Element {
  const history = useHistory();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const sendForApprovalMutation = useSendForApproval();
  const normalizedIds = Array.isArray(ids) ? ids : [ids];
  const initialValues = {
    username: "",
    password: "",
    // currentDate: moment().format("MM-DD-YYYY"),
    orderIds: normalizedIds,
  };

  async function sendForApproval(values: any) {
    if (submitting) return;
    setSubmitting(true);

    const payload = {
      orderIds: [].concat(values.orderIds).map((id: any) => Number(id)),
      username: values.username,
      password: values.password,
    };

    try {
      await sendForApprovalMutation.mutateAsync(payload);
      
      // Navigation is handled by the hook's onSuccess
      const response = sendForApprovalMutation.data;
      if (response?.data?.statuscode === 200) {
        history.push("/approvals");
      } else {
        history.push("/access/approvals/order-authorized");
      }
    } catch (error: any) {
      // useSendForApproval's onError already shows the backend message (e.g. "Credentials are not verified")
      setSubmitting(false);
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      isInitialValid={false}
      validationSchema={validations.approvalOrderValidation}
      onSubmit={sendForApproval}
      render={({
        errors,
        touched,
        setFieldValue,
        isValid,
        values,
        isSubmitting,
        submitForm,
        handleChange,
        handleBlur,
      }) => {
        return (
          <Form>
            <div className="border border-roundedInputBorder p-4 rounded-md bg-sideNavGray my-3 tab:mt-9">
              <h3 className="font-semibold !text-sm text-royalBlue">
                Authorize as clinician
              </h3>
              <h5 className="!text-sm text-royalBlue py-3">
                Please enter the clinician credentials below.
              </h5>
              <AuthorizePhysicianFields
                showPhysicianFields={false}
                values={values}
                touched={touched}
                errors={errors}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
              <div className="w-full flex flex-col justify-end items-end">
                <Button
                  buttonTheme="primary"
                  textInformation={{
                    text: `Submit as clinician`,
                    fontWeight: "font-semibold",
                  }}
                  className="mr-3"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                  onClick={isSubmitting ? () => { } : submitForm}
                />
              </div>
              {hasIDG && (
                <p className="text-royalBlue text-xs mt-5">
                  By my signature below, I, the healthcare provider, authorize
                  performance of NeuroIDgenetix 902 and indicate that the
                  purpose of the test(s), the procedures, the benefits and risks
                  involved have been explained to the patient and patient
                  informed consent has been obtained in accordance with state
                  and local laws. I attest that this patient has a diagnosis for
                  which pharmacologic therapy is reasonable and necessary, and
                  that the drug or drugs that I intend to prescribe are
                  reasonable and necessary for the treatment of this patient's
                  diagnosis. I have made an initial personalized decision for
                  this patient based on the patient's non-genetic factors. I
                  have a record of what drug(s) is/are being considered and for
                  what indication(s) to ensure the test(s) performed is
                  reasonable and necessary. The test results will be utilized by
                  me to determine a dose change to the patient's current
                  medication and/or prescribe a new medication. I have informed
                  the patient that Castle Biosciences, Inc. may be an
                  out-of-network provider under their insurance plan and that I
                  do not have any financial interest in Castle Biosciences, Inc.
                </p>
              )}
            </div>
          </Form>
        );
      }}
    />
  );
}

function ManualSubmission({
  ids,
  labels,
  hasIDG,
  downloading,
  downloadOrders,
  isMobileTable,
}: {
  ids: any;
  labels: any[];
  hasIDG: boolean;
  downloading: boolean;
  downloadOrders: () => any;
  isMobileTable?: boolean;
}): JSX.Element {
  const [files, setFiles] = useState<any>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [blockedModalVisible, setBlockedModalVisible] = useState(false);
  const [blockedDisplayName, setBlockedDisplayName] = useState<string | undefined>(undefined);
  const [blockedFailureItems, setBlockedFailureItems] = useState<
    ReadonlyArray<{ orderLabel: string; fileName: string; rowKey: string }> | null
  >(null);
  /** Row keys to clear from Formik + files when the blocked modal is dismissed (failed verification). */
  const [blockedRowKeysToClear, setBlockedRowKeysToClear] = useState<string[] | null>(null);
  const setFieldValueRef = useRef<
    ((field: string, value: unknown, shouldValidate?: boolean) => void) | null
  >(null);
  const [patientCheckRowKey, setPatientCheckRowKey] = useState<string | null>(null);
  const getPresignedUrl = useGetPresignedUrl();
  const addAuthorizedDocuments = useAddAuthorizedDocuments();
  const patientInfoCheck = useClassifyAnalyzeDocumentPatientInfoCheck();
  const normalizedIds = Array.isArray(ids) ? ids : [ids];
  const normalizedLabels = Array.isArray(labels)
    ? labels
    : labels
      ? [labels]
      : [];
  const isMultiOrder = normalizedIds.length > 1;

  const rowOrderLabel = (index: number, testId: number) =>
    `${index + 1}. ${normalizedLabels[index]?.patientName ?? `Order ${testId}`} - ${normalizedLabels[index]?.testName ?? "Test"}`;

  const initialValues = {
    username: "",
    password: "",
    orderIds: normalizedIds,
  };

  function dismissSubmissionBlockedModal() {
    const keys =
      blockedRowKeysToClear ??
      blockedFailureItems?.map((i) => i.rowKey).filter((k): k is string => Boolean(k));
    if (keys?.length) {
      keys.forEach((key) => setFieldValueRef.current?.(key, null));
      setFiles((prev: any[]) => prev.filter((f: any) => !keys.includes(f.name)));
    }
    setBlockedModalVisible(false);
    setBlockedDisplayName(undefined);
    setBlockedFailureItems(null);
    setBlockedRowKeysToClear(null);
  }

  async function authorizeOrderWithPDF() {
    if (submitting) return;
    setSubmitting(true);

    try {
      if (isMultiOrder) {
        const rows = normalizedIds.map((testId: number, index: number) => ({
          testId,
          index,
          uniqueKey: `${testId}_${index}`,
          orderLabel: rowOrderLabel(index, testId),
        }));

        const settled = await Promise.allSettled(
          rows.map(async (row) => {
            const fileEntry = files.find((f: any) => f.name === row.uniqueKey);
            if (!fileEntry?.value) {
              throw new Error("Missing file");
            }
            const file = fileEntry.value as File;
            const azureName = file.name.replace(/\s+/g, "_");
            const presignedUrlResponse = await getPresignedUrl.mutateAsync(azureName);
            const { url, filepath } = presignedUrlResponse.data;
            await axios.put(url, file, {
              headers: {
                "Content-Type": file.type,
                "x-ms-blob-type": "BlockBlob",
              },
            });
            const checkData = await patientInfoCheck.mutateAsync(filepath);
            if (checkData?.complete === false) {
              throw new Error("Patient information incomplete");
            }
            return { filePath: filepath, orderId: String(row.testId) };
          })
        );

        const failures: Array<{ orderLabel: string; fileName: string; rowKey: string }> = [];
        const uploadedFiles: Array<{ filePath: string; orderId: string }> = [];

        settled.forEach((result, i) => {
          const row = rows[i];
          const fileEntry = files.find((f: any) => f.name === row.uniqueKey);
          const fileName = fileEntry?.value?.name ?? "Unknown file";
          if (result.status === "fulfilled") {
            uploadedFiles.push(result.value);
          } else {
            failures.push({ orderLabel: row.orderLabel, fileName, rowKey: row.uniqueKey });
          }
        });

        if (failures.length > 0) {
          setBlockedFailureItems(failures);
          setBlockedRowKeysToClear(failures.map((f) => f.rowKey));
          setBlockedDisplayName(undefined);
          setBlockedModalVisible(true);
          return;
        }

        await addAuthorizedDocuments.mutateAsync({ files: uploadedFiles });
        return;
      }

      const uploadedFiles = await Promise.all(
        files.map(async (file: any) => {
          if (file.azurePath && file.patientInfoOk) {
            return {
              filePath: file.azurePath,
              orderId: file.orderId,
            };
          }
          const fileName = file?.value?.name.replace(/\s+/g, "_");

          const presignedUrlResponse = await getPresignedUrl.mutateAsync(fileName);
          const { url, filepath } = presignedUrlResponse.data;

          await axios.put(url, file.value, {
            headers: {
              "Content-Type": file.value.type,
              "x-ms-blob-type": "BlockBlob",
            },
          });
          return {
            filePath: filepath,
            orderId: file.orderId,
          };
        })
      );

      for (let i = 0; i < uploadedFiles.length; i++) {
        if (files[i]?.patientInfoOk) continue;
        const checkData = await patientInfoCheck.mutateAsync(uploadedFiles[i].filePath);
        if (checkData?.complete === false) {
          setBlockedFailureItems(null);
          setBlockedRowKeysToClear(
            files[i]?.name ? [String(files[i].name)] : null
          );
          setBlockedDisplayName(files[i]?.value?.name ?? undefined);
          setBlockedModalVisible(true);
          return;
        }
      }

      const data = {
        files: uploadedFiles,
      };

      await addAuthorizedDocuments.mutateAsync(data);
    } catch (error: any) {
      if (error.response) {
        toastMessages.error(
          error.response.data.message ?? "Authorization using pdf failed"
        );
      } else {
        toastMessages.error(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <Fragment>
    <Formik
      initialValues={initialValues}
      isInitialValid={false}
      validationSchema={validations.approvalOrderValidation}
      onSubmit={() => { }}
      render={({
        errors,
        touched,
        setFieldValue,
        isValid,
        values,
        initialValues,
        setFieldError,
        validateField,
      }) => {
        setFieldValueRef.current = setFieldValue;
        return (
          <Form>
            <div className="border border-roundedInputBorder p-4 rounded-md bg-sideNavGray my-3">
              <h3 className="font-semibold !text-sm text-royalBlue">
                Download Orders
              </h3>
              <h5 className="!text-sm text-royalBlue py-3">
                Please download all test order's PDF for authorization.
              </h5>

              <Button
                buttonTheme="secondary"
                textInformation={{
                  text: `Download all`,
                  fontWeight: "font-semibold",
                }}
                className="py-1.5 bg-white"
                disabled={
                  submitting ||
                  downloading ||
                  (!isMultiOrder && patientCheckRowKey !== null)
                }
                loading={downloading}
                onClick={downloadOrders}
              />

              <div className="mb-2 mt-6">
                <h3 className="font-semibold !text-sm text-royalBlue">
                  Upload Authorized Order
                </h3>
                <h5 className="!text-sm text-royalBlue py-3">
                  Please upload the scan of the signed test order by the
                  clinician below. Accepted formats include .png, .jpeg, .pdf,
                  and .doc/*, with a maximum file size of 50 MB.
                </h5>
                {((!isMultiOrder && patientCheckRowKey !== null) || submitting) && (
                  <div className="mb-3 rounded-md border border-primary bg-lightBlue px-3 py-2">
                    <CustomText
                      text={
                        isMultiOrder && submitting
                          ? "Uploading and verifying patient information for all orders in parallel. This can take up to a minute — please wait."
                          : patientCheckRowKey !== null
                          ? "Uploading your file and verifying patient information. This can take up to a minute — please wait before choosing another file or submitting."
                          : "Submitting authorization…"
                      }
                      fontWeight="font-medium"
                      fontSize="text-sm"
                      className="text-royalBlue"
                    />
                  </div>
                )}
                <div className="grid grid-cols-[repeat(auto-fill,220px)] gap-4 my-3 w-full justify-center md:justify-start">
                  {normalizedIds.map((testId: number, index: number) => {
                    // Create a truly unique identifier for this specific row
                    const uniqueKey = `${testId}_${index}`;

                    return (
                      <FileUploader
                        key={uniqueKey} // Fixes React duplicate key warnings
                        name={uniqueKey} // Fixes the setFiles finding the wrong index
                        id={uniqueKey} // Fixes the label triggering the wrong input
                        className="w-full min-w-0"
                        labelInfo={{
                          label: `${index +
                            1 +
                            ". " +
                            (normalizedLabels[index]?.patientName ||
                              `Order ${testId}`) +
                            " - " +
                            (normalizedLabels[index]?.testName || "Test")
                            }`,
                        }}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          const { filePath } = e.target;
                          setFieldValue(name, value);
                          setFiles((prevFiles: any[]) => {
                            const fileIndex = prevFiles.findIndex(
                              (file) => file.name === name
                            );

                            if (fileIndex !== -1) {
                              const updatedFiles = [...prevFiles];
                              updatedFiles[fileIndex] = {
                                name,
                                value,
                                orderId: testId,
                                filePath,
                                azurePath: undefined,
                                patientInfoOk: false,
                              };
                              return updatedFiles;
                            }
                            return [
                              ...prevFiles,
                              {
                                name,
                                value,
                                orderId: testId,
                                filePath,
                                azurePath: undefined,
                                patientInfoOk: false,
                              },
                            ];
                          });

                          if (value instanceof File && !isMultiOrder) {
                            void (async () => {
                              const rowKey = name;
                              setPatientCheckRowKey(rowKey);
                              try {
                                const fileName = value.name.replace(/\s+/g, "_");
                                const presignedUrlResponse =
                                  await getPresignedUrl.mutateAsync(fileName);
                                const { url, filepath } = presignedUrlResponse.data;
                                await axios.put(url, value, {
                                  headers: {
                                    "Content-Type": value.type,
                                    "x-ms-blob-type": "BlockBlob",
                                  },
                                });
                                const checkData = await patientInfoCheck.mutateAsync(filepath);
                                if (checkData?.complete === false) {
                                  setBlockedFailureItems(null);
                                  setBlockedRowKeysToClear(null);
                                  setBlockedDisplayName(value.name);
                                  setBlockedModalVisible(true);
                                  setFieldValue(rowKey, null);
                                  setFiles((prev: any[]) => prev.filter((f: any) => f.name !== rowKey));
                                  return;
                                }
                                setFiles((prev: any[]) =>
                                  prev.map((f: any) =>
                                    f.name === rowKey
                                      ? {
                                          ...f,
                                          azurePath: filepath,
                                          patientInfoOk: true,
                                          orderId: testId,
                                        }
                                      : f
                                  )
                                );
                              } catch (err: any) {
                                const msg =
                                  err?.response?.data?.message ??
                                  err?.message ??
                                  "Upload or patient information check failed.";
                                toastMessages.error(msg);
                                setFieldValue(rowKey, null);
                                setFiles((prev: any[]) => prev.filter((f: any) => f.name !== rowKey));
                              } finally {
                                setPatientCheckRowKey(null);
                              }
                            })();
                          }
                        }}
                        setFieldTouched={setFieldValue}
                        value={
                          files?.find((f: any) => f.name === uniqueKey)?.value
                        }
                        approval={true}
                        isMobileTable={isMobileTable}
                        disabled={
                          (isMultiOrder ? submitting : patientCheckRowKey !== null || submitting)
                        }
                      />
                    );
                  })}
                </div>
                <div className="w-full flex flex-col justify-end items-end">
                  <Button
                    buttonTheme="primary"
                    textInformation={{
                      text: `Submit`,
                      fontWeight: "font-semibold",
                    }}
                    className="mb-0"
                    loading={submitting}
                    disabled={
                      submitting ||
                      (!isMultiOrder && patientCheckRowKey !== null) ||
                      (files &&
                        files.length > 0 &&
                        files.some((item: any) => item.value === null)) ||
                      files.length === 0 ||
                      normalizedIds.length !== files.length
                    }
                    onClick={authorizeOrderWithPDF}
                  />
                </div>
                {hasIDG && (
                  <p className="text-royalBlue text-xs mt-5">
                    By my signature below, I, the healthcare provider, authorize
                    performance of NeuroIDgenetix 902 and indicate that the
                    purpose of the test(s), the procedures, the benefits and
                    risks involved have been explained to the patient and
                    patient informed consent has been obtained in accordance
                    with state and local laws. I attest that this patient has a
                    diagnosis for which pharmacologic therapy is reasonable and
                    necessary, and that the drug or drugs that I intend to
                    prescribe are reasonable and necessary for the treatment of
                    this patient's diagnosis. I have made an initial
                    personalized decision for this patient based on the
                    patient's non-genetic factors. I have a record of what
                    drug(s) is/are being considered and for what indication(s)
                    to ensure the test(s) performed is reasonable and necessary.
                    The test results will be utilized by me to determine a dose
                    change to the patient's current medication and/or prescribe
                    a new medication. I have informed the patient that Castle
                    Biosciences, Inc. may be an out-of-network provider under
                    their insurance plan and that I do not have any financial
                    interest in Castle Biosciences, Inc.
                  </p>
                )}
              </div>
            </div>
          </Form>
        );
      }}
    />
    <BlankDocumentSubmissionBlockedModal
      visible={blockedModalVisible}
      onClose={dismissSubmissionBlockedModal}
      onReplaceFile={dismissSubmissionBlockedModal}
      issueItems={blockedFailureItems ?? undefined}
      issueQuotedFileName={blockedDisplayName}
    />
    </Fragment>
  );
}

export default function ApprovalPopup({
  closePopup,
  submitting,
  ids,
  hasIdg,
  downloadOrders,
  downloading,
  labels,
  hideManualSubmission,
  isMobileTable,
}: any) {
  // Normalize ids to always be an array
  const normalizedIds = Array.isArray(ids) ? ids : [ids];
  const normalizedLabels = Array.isArray(labels)
    ? labels
    : labels
      ? [labels]
      : [];

  return (
    <div
      className={`relative max-h-[85vh] w-4/5 max-w-2xl bg-white p-6 rounded shadow-lg overflow-auto`}
    >
      <div className="flex justify-between mb-4">
        <h2 className="text-xl font-semibold">Authorization </h2>
        <CloseIcon onClick={closePopup} className="cursor-pointer" />
      </div>
      <AuthorizeAsPhysician ids={normalizedIds} hasIDG={hasIdg} />

      {!!hideManualSubmission === false && (
        <Fragment>
          <div className="w-full flex flex-col justify-center items-center my-2">
            <div className="h-[16px] bg-dropDownBorder w-[1px]" />
            <p className="text-royalBlue !text-sm">OR</p>
            <div className="h-[16px] bg-dropDownBorder w-[1px]" />
          </div>
          <ManualSubmission
            ids={normalizedIds}
            hasIDG={hasIdg}
            downloading={downloading}
            downloadOrders={downloadOrders}
            labels={normalizedLabels}
            isMobileTable={isMobileTable}
          />
        </Fragment>
      )}
    </div>
  );
}
